import { parseThemeBlock, type ThemeBlock } from "../shared/presentation";
import { boundedKeys, exactKeys, GameSpecValidationError, integer, record, text } from "../shared/validators";

export interface CardTurnSpec {
  readonly version: 1;
  readonly kind: "card-turn";
  readonly title: string;
  readonly seed: number;
  readonly theme: ThemeBlock;
  readonly params: {
    readonly deckSize: number;
    readonly roundsToWin: number;
    /** 1 = versus the seeded rival (default); 2 = local duel, both open hands on one screen, alternating initiative. */
    readonly players?: number;
  };
}

/** A numeric card duel: each round the rival reveals a card first, then the player answers from their own hand. */
export function parseCardTurnSpec(root: Record<string, unknown>): CardTurnSpec {
  exactKeys(root, ["version", "kind", "title", "seed", "theme", "params"], "game spec");
  if (root.kind !== "card-turn") throw new GameSpecValidationError("unsupported game kind");

  const params = record(root.params, "params");
  boundedKeys(params, ["deckSize", "roundsToWin"], ["players"], "params");
  const players = params.players === undefined ? undefined : integer(params.players, "params.players", 1, 2);

  const deckSize = integer(params.deckSize, "params.deckSize", 10, 20);
  const roundsToWin = integer(params.roundsToWin, "params.roundsToWin", Math.floor(deckSize / 2) + 1, deckSize);

  return {
    version: 1,
    kind: "card-turn",
    title: text(root.title, "title", 1, 80),
    seed: integer(root.seed, "seed", 0, 4_294_967_295),
    theme: parseThemeBlock(root.theme),
    params: { deckSize, roundsToWin, ...(players === undefined ? {} : { players }) },
  };
}
